import { getDatabase, ref, get, set } from 'firebase/database';
import { BuildingCheckTemplate } from './types';

export interface BuildingCheck {
    templateId: string;
    startDate: string;
    nextDueDate: string;
    addedBy: string;
    addedTime: string;
    lastCompletedDate?: string;        // Date of last completion (YYYY-MM-DD)
    lastMeasurement?: {                // Last measurement value (if applicable)
        value: number;
        unit: string;
        withinRange: boolean;
    };
}

export interface BuildingCheckWithTemplate extends BuildingCheck {
    template: BuildingCheckTemplate;
    buildingId?: string;
    buildingName?: string;
}

export interface CheckCompletion {
    templateId: string;
    completedBy: string;
    completedAt: string;        // When the work was actually done (can be backdated)
    submittedAt: string;        // When the data was submitted to the system
    actualDueDate: string;      // The date it was actually due
    nextDueDate: string;        // Calculated next due date
    measurement?: {
        value: number;
        unit: string;
        withinRange: boolean;
    };
    notes?: string;             // Free text notes
    photos?: string[];          // URLs to uploaded photos
}

export const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('sv-SE');
};

export const formatInterval = (interval: { value: number; unit: string }) => {
    const unitTranslations: { [key: string]: string } = {
        'days': 'dagar',
        'weeks': 'veckor', 
        'months': 'månader',
        'years': 'år'
    };
    
    const swedishUnit = unitTranslations[interval.unit] || interval.unit;
    return `Var ${interval.value} ${swedishUnit}`;
};

export const getDaysUntilDue = (nextDueDate: string) => {
    const today = new Date();
    const dueDate = new Date(nextDueDate);
    const daysUntilDue = Math.ceil((dueDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
    return daysUntilDue;
};

export const formatDaysUntilDue = (nextDueDate: string) => {
    const days = getDaysUntilDue(nextDueDate);
    if (days < 0) {
        return `${Math.abs(days)} dagar försenad`;
    } else if (days === 0) {
        return 'Förfaller idag';
    } else {
        return `${days} dagar kvar`;
    }
};

export const calculateNextDueDate = (template: BuildingCheckTemplate, completedAt: string, currentDueDate: string): string => {
    const completedAtDate = new Date(completedAt);
    const currentDueDateDate = new Date(currentDueDate);
    
    // For "actual" calculation: simply add interval to completion date
    if (template.nextDueDateCalculation === 'actual') {
        const nextDate = new Date(completedAt);
        switch (template.interval.unit) {
            case 'days':
                nextDate.setDate(nextDate.getDate() + template.interval.value);
                break;
            case 'weeks':
                nextDate.setDate(nextDate.getDate() + (template.interval.value * 7));
                break;
            case 'months':
                nextDate.setMonth(nextDate.getMonth() + template.interval.value);
                break;
            case 'years':
                nextDate.setFullYear(nextDate.getFullYear() + template.interval.value);
                break;
        }
        return nextDate.toISOString().split('T')[0];
    }
    
    // For "scheduled" calculation: maintain the regular cadence
    // Start from the current due date and keep adding intervals until we find
    // the next occurrence that's after the completion date
    let nextDate = new Date(currentDueDate);
    
    // Keep adding intervals until we get a date that's after the completion date
    // This maintains the schedule cadence (e.g., always on the 1st of the month)
    // Example:
    // - Due: 2024-01-01, Completed: 2024-03-15 (very late!)
    // - Interval: 1 month
    // - Next candidates: 2024-02-01 (before completion), 2024-03-01 (before completion), 2024-04-01 ✓
    while (nextDate <= completedAtDate) {
        switch (template.interval.unit) {
            case 'days':
                nextDate.setDate(nextDate.getDate() + template.interval.value);
                break;
            case 'weeks':
                nextDate.setDate(nextDate.getDate() + (template.interval.value * 7));
                break;
            case 'months':
                nextDate.setMonth(nextDate.getMonth() + template.interval.value);
                break;
            case 'years':
                nextDate.setFullYear(nextDate.getFullYear() + template.interval.value);
                break;
        }
    }
    
    const calculatedNextDate = nextDate.toISOString().split('T')[0];
    
    // Safety check: never move backwards from current due date (for backdated entries)
    // Example: Current due 2024-03-01, user backdates completion to 2024-01-15
    // We should keep 2024-03-01, not calculate backwards to 2024-02-01
    if (calculatedNextDate < currentDueDate) {
        return currentDueDate;
    }
    
    return calculatedNextDate;
};

export const updateActiveCheckWithLastCompletion = async (
    buildingId: string, 
    templateId: string, 
    completedAt: string, 
    nextDueDate: string,
    measurement?: { value: number; unit: string; withinRange: boolean }
) => {
    const db = getDatabase();
    const activeCheckRef = ref(db, `buildingChecks/active/${buildingId}/${templateId}`);
    
    // Get current active check data
    const currentSnapshot = await get(activeCheckRef);
    if (!currentSnapshot.exists()) {
        throw new Error('Active check not found');
    }
    
    const currentData = currentSnapshot.val();
    
    // Only update lastCompletedDate and lastMeasurement if the new completion date is newer than the current one
    const shouldUpdateLastCompletion = !currentData.lastCompletedDate || completedAt >= currentData.lastCompletedDate;
    
    // Update with new completion data
    const updatedData = {
        ...currentData,
        nextDueDate,
        ...(shouldUpdateLastCompletion && { 
            lastCompletedDate: completedAt,
            ...(measurement && { lastMeasurement: measurement })
        })
    };
    
    await set(activeCheckRef, updatedData);
};

export const loadBuildingChecks = async (buildingId: string, buildingName: string) => {
    const db = getDatabase();
    try {
        const checksRef = ref(db, `buildingChecks/active/${buildingId}`);
        const checksSnapshot = await get(checksRef);
        
        if (checksSnapshot.exists()) {
            const checksData = checksSnapshot.val();
            const checks: BuildingCheckWithTemplate[] = [];
            
            // Load template data for each check
            for (const [templateId, checkData] of Object.entries(checksData)) {
                const templateRef = ref(db, `buildingChecks/templates/${templateId}`);
                const templateSnapshot = await get(templateRef);
                
                if (templateSnapshot.exists()) {
                    const templateData = templateSnapshot.val();
                    checks.push({
                        ...(checkData as BuildingCheck),
                        templateId,
                        template: templateData,
                        buildingId,
                        buildingName
                    });
                }
            }
            
            return checks;
        }
        return [];
    } catch (error) {
        console.error('Error loading building checks:', error);
        return [];
    }
};

export const loadAllBuildingChecks = async (buildings: {key: string, name: string}[]) => {
    const db = getDatabase();
    const allChecks: BuildingCheckWithTemplate[] = [];
    
    try {
        // Load entire buildingChecks/active and buildingChecks/templates nodes at once
        const [activeChecksSnapshot, templatesSnapshot] = await Promise.all([
            get(ref(db, 'buildingChecks/active')),
            get(ref(db, 'buildingChecks/templates'))
        ]);
        
        // Create building lookup map for quick access
        const buildingsMap = new Map(buildings.map(b => [b.key, b]));
        
        // Process active checks data
        if (activeChecksSnapshot.exists()) {
            const activeChecksData = activeChecksSnapshot.val();
            
            // Process templates data
            const templatesMap: {[templateId: string]: BuildingCheckTemplate} = {};
            if (templatesSnapshot.exists()) {
                const templatesData = templatesSnapshot.val();
                Object.entries(templatesData).forEach(([templateId, templateData]: [string, any]) => {
                    templatesMap[templateId] = { id: templateId, ...templateData };
                });
            }
            
            // Build final checks array
            Object.entries(activeChecksData).forEach(([buildingId, buildingChecks]: [string, any]) => {
                const building = buildingsMap.get(buildingId);
                if (building && buildingChecks) {
                    Object.entries(buildingChecks).forEach(([templateId, checkData]: [string, any]) => {
                        const template = templatesMap[templateId];
                        if (template) {
                            allChecks.push({
                                ...checkData,
                                template,
                                buildingId: building.key,
                                buildingName: building.name
                            });
                        }
                    });
                }
            });
        }
        
        // Sort by next due date (chronological order)
        return allChecks.sort((a, b) => {
            return new Date(a.nextDueDate).getTime() - new Date(b.nextDueDate).getTime();
        });
        
    } catch (error) {
        console.error('Error loading all building checks:', error);
        return [];
    }
};
